import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { doc, setDoc, deleteDoc, onSnapshot, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

/**
 * TimerContext — app-wide shared live timer for sleep and breastfeeding sessions.
 *
 * Timer state is stored in Firestore at activeTimers/{childId} so it is visible
 * to all caregivers on any device in real time. onSnapshot keeps every connected
 * client in sync automatically.
 *
 * Only one timer can run per child at a time — using childId as the Firestore
 * document ID makes this structurally impossible to violate. setDoc is atomic
 * so two simultaneous starts resolve to one winner with no orphaned state.
 *
 * Usage:
 *   const { timer, elapsed, start, stop, cancel } = useTimer();
 *
 *   timer   — null | { type: 'sleep'|'breastfeeding', startTime: ISO string }
 *   elapsed — seconds since startTime (client-side tick, updated every second)
 *   start(type) — begins a new timer; no-op if one is already running
 *   stop()  — clears timer and returns { type, startTime, elapsedSeconds }
 *   cancel()— clears timer without returning data
 */
const TimerContext = createContext(null);

export function TimerProvider({ children, childId }) {
  const { user, profile } = useAuth();
  const [timerData, setTimerData] = useState(null); // { type, startTime }
  const [elapsed, setElapsed]     = useState(0);

  // ── Real-time listener ─────────────────────────────────────────────────────
  // Subscribes to activeTimers/{childId}. When any caregiver writes or deletes
  // the document, every connected client receives the update within milliseconds.
  useEffect(() => {
    if (!childId) {
      setTimerData(null);
      setElapsed(0);
      return;
    }

    const ref  = doc(db, 'activeTimers', childId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const { type, startTime, wakeUps } = snap.data();
          setTimerData({ type, startTime, wakeUps: wakeUps || [] });
          setElapsed(Math.floor((Date.now() - new Date(startTime)) / 1000));
        } else {
          setTimerData(null);
          setElapsed(0);
        }
      },
      (err) => {
        console.error('Timer listener error:', err);
      }
    );

    return () => unsub();
  }, [childId]);

  // ── Client-side tick ───────────────────────────────────────────────────────
  // Recalculates elapsed every second from the stored startTime.
  // Firestore is not polled — only the local clock ticks.
  useEffect(() => {
    if (!timerData) { setElapsed(0); return; }
    const tick = () =>
      setElapsed(Math.floor((Date.now() - new Date(timerData.startTime)) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timerData]);

  // ── start ──────────────────────────────────────────────────────────────────
  // Optimistic local update gives instant feedback; Firestore write syncs to
  // every other caregiver. No-op if a timer is already running.
  const start = useCallback((type) => {
    if (!childId || timerData) return;
    const startTime = new Date().toISOString();
    const t = { type, startTime };

    setTimerData(t);
    setElapsed(0);

    setDoc(doc(db, 'activeTimers', childId), {
      type,
      startTime,
      wakeUps: [],
      startedBy: {
        uid:         user?.uid          ?? '',
        displayName: profile?.displayName ?? user?.email ?? '',
        email:       user?.email        ?? '',
      },
      createdAt: startTime,
    }).catch((err) => {
      console.error('Timer start failed:', err);
      // Roll back the optimistic update if the write fails
      setTimerData(null);
      setElapsed(0);
    });
  }, [childId, timerData, user, profile]);

  // ── stop ───────────────────────────────────────────────────────────────────
  // Captures result from in-memory state (no round-trip needed), applies
  // optimistic local update, then removes the Firestore doc so all other
  // caregivers' onSnapshot listeners fire and their banners disappear.
  const stop = useCallback(() => {
    if (!timerData || !childId) return null;

    const result = {
      type:           timerData.type,
      startTime:      timerData.startTime,
      elapsedSeconds: Math.floor((Date.now() - new Date(timerData.startTime)) / 1000),
      wakeUps:        timerData.wakeUps || [],
    };

    setTimerData(null);
    setElapsed(0);

    deleteDoc(doc(db, 'activeTimers', childId)).catch((err) => {
      console.error('Timer stop failed:', err);
    });

    return result;
  }, [timerData, childId]);

  // ── addWakeUp ──────────────────────────────────────────────
  // Appends a wake-up entry to the activeTimers doc. The onSnapshot listener
  // picks up the change so all caregivers see the updated count in real time.
  const addWakeUp = useCallback(async (wakeUp) => {
    if (!childId || !timerData) return;
    try {
      await updateDoc(doc(db, 'activeTimers', childId), {
        wakeUps: arrayUnion(wakeUp),
      });
    } catch (err) {
      console.error('addWakeUp failed:', err);
    }
  }, [childId, timerData]);

  // ── cancel ─────────────────────────────────────────────────────────────────
  const cancel = useCallback(() => {
    if (!childId) return;
    setTimerData(null);
    setElapsed(0);
    deleteDoc(doc(db, 'activeTimers', childId)).catch((err) => {
      console.error('Timer cancel failed:', err);
    });
  }, [childId]);

  return (
    <TimerContext.Provider value={{ timer: timerData, elapsed, start, stop, cancel, addWakeUp }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within a TimerProvider');
  return ctx;
}
